<script lang="ts">
export default {
  name: 'GenericMultiFilter',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import FilterButtonV2 from '@/components/v2/FilterButton.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Checkbox from '@/components/v2/Checkbox.vue';

export interface GenericMultiFilterOption {
  label: string;
  value: string | number;
}

export interface GenericMultiFilterToggleValue {
  value: string | number;
  isIncluded: boolean;
}

const props = withDefaults(
  defineProps<{
    /** Options to show in the multi-select (label + value). */
    options: GenericMultiFilterOption[];
    modelValue?: (string | number)[];
    title: string;
    /** When set (e.g. "label"), chip shows up to 2 selected labels when 1–2 selected; otherwise shows title. */
    chipTitleField?: keyof GenericMultiFilterOption;
    dataTest?: string;
    dataTestPrefix?: string;
    /** Max height of the scrollable list (default 300px). */
    maxListHeight?: string;
    /** Number of columns for the checkbox grid (default 2). */
    columns?: number;
    /** When true, each row gets an "is / is not" toggle and the component emits via v-model:toggleValue. */
    enableIncludeExcludeToggle?: boolean;
    /** v-model:toggleValue — required when enableIncludeExcludeToggle is true. */
    toggleValue?: GenericMultiFilterToggleValue[];
  }>(),
  {
    modelValue: () => [],
    chipTitleField: undefined,
    dataTestPrefix: 'generic-multifilter',
    maxListHeight: '300px',
    columns: 2,
    enableIncludeExcludeToggle: false,
    toggleValue: () => [],
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: (string | number)[]];
  'update:toggleValue': [value: GenericMultiFilterToggleValue[]];
}>();

const activeCount = computed(() =>
  props.enableIncludeExcludeToggle ? props.toggleValue.length : props.modelValue.length,
);

const chipTitle = computed(() => {
  if (!props.chipTitleField || activeCount.value === 0 || activeCount.value > 2) {
    return props.title;
  }
  const field = props.chipTitleField;
  const selectedValues = props.enableIncludeExcludeToggle
    ? props.toggleValue.map((t) => t.value)
    : props.modelValue;
  const labels = props.options
    .filter((o) => selectedValues.some((v) => v === o.value))
    .slice(0, 2)
    .map((o) => o[field] as string);
  return labels.join(', ') || props.title;
});

const draft = ref<(string | number)[]>([]);
const draftToggle = ref<GenericMultiFilterToggleValue[]>([]);

watch(
  () => props.modelValue,
  (val) => {
    draft.value = [...(val ?? [])];
  },
  { immediate: true },
);

watch(
  () => props.toggleValue,
  (val) => {
    draftToggle.value = [...(val ?? [])];
  },
  { immediate: true },
);

const draftToggleMap = computed(() => new Map(draftToggle.value.map((t) => [t.value, t.isIncluded])));

function toggle(value: string | number, checked: boolean) {
  if (checked) {
    if (!draft.value.includes(value)) draft.value = [...draft.value, value];
  } else {
    draft.value = draft.value.filter((v) => v !== value);
  }
}

function toggleWithInclude(value: string | number, checked: boolean) {
  if (checked) {
    if (!draftToggle.value.some((t) => t.value === value)) {
      draftToggle.value = [...draftToggle.value, { value, isIncluded: true }];
    }
  } else {
    draftToggle.value = draftToggle.value.filter((t) => t.value !== value);
  }
}

function setIncluded(value: string | number, isIncluded: boolean) {
  const existing = draftToggle.value.find((t) => t.value === value);
  if (existing) {
    draftToggle.value = draftToggle.value.map((t) => (t.value === value ? { ...t, isIncluded } : t));
  } else {
    draftToggle.value = [...draftToggle.value, { value, isIncluded }];
  }
}

function isChecked(value: string | number): boolean {
  return props.enableIncludeExcludeToggle ? draftToggleMap.value.has(value) : draft.value.includes(value);
}

function getIncluded(value: string | number): boolean | undefined {
  return draftToggleMap.value.get(value);
}

function clear(saveFn: (payload?: unknown) => void) {
  if (props.enableIncludeExcludeToggle) {
    draftToggle.value = [];
    emit('update:toggleValue', []);
    saveFn([]);
  } else {
    draft.value = [];
    emit('update:modelValue', []);
    saveFn([]);
  }
}

function selectAll() {
  if (props.enableIncludeExcludeToggle) {
    draftToggle.value = props.options.map((o) => {
      const existing = draftToggle.value.find((t) => t.value === o.value);
      return existing ? { ...existing } : { value: o.value, isIncluded: true };
    });
  } else {
    draft.value = props.options.map((o) => o.value);
  }
}

function apply(saveFn: (payload?: unknown) => void) {
  if (props.enableIncludeExcludeToggle) {
    const payload = draftToggle.value.map((t) => ({ ...t }));
    emit('update:toggleValue', payload);
    saveFn(payload);
  } else {
    emit('update:modelValue', [...draft.value]);
    saveFn([...draft.value]);
  }
}
</script>

<template>
  <FilterButtonV2
    :title="chipTitle"
    :active="activeCount > 0"
    start
    :data-test-activator="dataTest"
    @save="
      (payload) => {
        if (!Array.isArray(payload)) return;
        if (enableIncludeExcludeToggle) {
          emit('update:toggleValue', payload as GenericMultiFilterToggleValue[]);
        } else {
          emit('update:modelValue', payload as (string | number)[]);
        }
      }
    ">
    <template #menu="{ save }">
      <div class="flex flex-col">
        <h4 class="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-800">
          {{ title }}
        </h4>
        <div
          class="overflow-y-auto px-4 py-3"
          :class="enableIncludeExcludeToggle || columns === 1 ? '' : 'grid gap-x-8 gap-y-1'"
          :style="{
            maxHeight: maxListHeight,
            gridTemplateColumns: !enableIncludeExcludeToggle && columns > 1 ? `repeat(${columns}, auto)` : undefined,
          }">
          <template v-if="enableIncludeExcludeToggle">
            <div
              v-for="opt in options"
              :key="String(opt.value)"
              class="-mx-1 flex items-center justify-between gap-3 rounded px-1 py-0.5 transition-colors"
              :class="
                isChecked(opt.value) && getIncluded(opt.value) === false
                  ? 'bg-red-50 hover:bg-red-100'
                  : 'hover:bg-gray-100'
              ">
              <Checkbox
                :model-value="isChecked(opt.value)"
                :label="opt.label"
                :class="
                  isChecked(opt.value) && getIncluded(opt.value) === false ? 'line-through text-red-700' : ''
                "
                :data-test="`${dataTestPrefix}-checkbox-${String(opt.value)}`"
                :data-testid="`${dataTestPrefix}-item-${String(opt.value)}`"
                @update:model-value="(v) => toggleWithInclude(opt.value, v)" />
              <div
                class="flex shrink-0 overflow-hidden rounded-full text-xs font-medium"
                :class="isChecked(opt.value) ? '' : 'opacity-50'">
                <button
                  type="button"
                  class="px-2.5 py-0.5 transition-colors"
                  :class="
                    isChecked(opt.value) && getIncluded(opt.value) !== false
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  "
                  :data-test="`${dataTestPrefix}-is-${String(opt.value)}`"
                  @click="setIncluded(opt.value, true)">
                  is
                </button>
                <button
                  type="button"
                  class="px-2.5 py-0.5 transition-colors"
                  :class="
                    isChecked(opt.value) && getIncluded(opt.value) === false
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  "
                  :data-test="`${dataTestPrefix}-is-not-${String(opt.value)}`"
                  @click="setIncluded(opt.value, false)">
                  is not
                </button>
              </div>
            </div>
          </template>
          <template v-else>
            <div
              v-for="opt in options"
              :key="String(opt.value)"
              class="-mx-1 rounded px-1 transition-colors hover:bg-gray-100">
              <Checkbox
                :model-value="draft.includes(opt.value)"
                :label="opt.label"
                :data-test="`${dataTestPrefix}-checkbox-${String(opt.value)}`"
                :data-testid="`${dataTestPrefix}-item-${String(opt.value)}`"
                @update:model-value="(v) => toggle(opt.value, v)" />
            </div>
          </template>
        </div>
        <div class="flex justify-between items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div class="flex gap-2 items-center">
            <AppButton
              outline
              :data-test="`${dataTestPrefix}-clear`"
              @click="clear(save)">
              Clear
            </AppButton>
            
          </div>
          <span class="flex gap-2">
            <slot name="menu-actions" :draft="draft" :save="save" />
            <AppButton
              secondary
              :data-test="`${dataTestPrefix}-select-all`"
              @click="selectAll">
              Select All
            </AppButton>
            <AppButton
              primary
              :data-test="`${dataTestPrefix}-apply`"
              @click="apply(save)">
              Apply
            </AppButton>
          </span>
        </div>
      </div>
    </template>
  </FilterButtonV2>
</template>
